import type { SVGProps } from "react";
import React, { useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import type { Province } from "@/store/types/province";
import { provinceIcons } from "./ProvinceIcons";
import "./index.css";
import { observer } from "mobx-react";
import { useStore } from "@/store";

interface Props {
  onClick?: (province: Province) => unknown;
  isInteractive?: boolean;
  provinceTranslations?: Record<Province, string>;
  svgProps?: SVGProps<any>;
}

export const CanadaMap = observer(
  ({ onClick, isInteractive, provinceTranslations, svgProps }: Props) => {
    const { activeProvinceId } = useStore();

    const [hoverProvince, setHoverProvince] = useState<Province | undefined>();
    const tooltipRef = useRef<HTMLDivElement>(null);

    const handleClick = (province: Province) => {
      if (onClick) onClick(province);
    };

    const handleMouseMove = (
      e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    ) => {
      const tooltip = tooltipRef.current;

      if (!tooltip) return;

      tooltip.style.left = `${e.pageX}px`;
      tooltip.style.top = `${e.pageY}px`;
    };

    const getProps = (province: Province) => {
      return isInteractive
        ? {
            onClick: () => handleClick(province),
            onMouseMove: handleMouseMove,
            onMouseEnter: () => setHoverProvince(province),
            onMouseLeave: () => setHoverProvince(undefined),
          }
        : {};
    };

    return (
      <Box
        maxW="container.md"
        alignSelf="center"
        width="100%"
      >
        <Box
          position="absolute"
          visibility={hoverProvince ? "visible" : "hidden"}
          background="primary"
          color="white"
          fontWeight="bold"
          px="2"
          py="1"
          mt="-40px"
          borderRadius="4px"
          ref={tooltipRef}
        >
          {hoverProvince &&
            provinceTranslations &&
            provinceTranslations[hoverProvince]}
        </Box>
        <svg
          className="CanadaMap"
          version="1.1"
          viewBox="0 0 363.4 298.4"
          style={{ background: "new 0 0 363.4 298.4" }}
          xmlSpace="preserve"
          width="100%"
          {...svgProps}
        >
          {provinceIcons.map((icon) =>
            React.createElement(icon.component, {
              key: icon.province,
              ...getProps(icon.province),
              className: [
                "CanadaMap-province",
                isInteractive ? "is-interactive" : "",
                activeProvinceId === icon.province ? "is-selected" : "",
              ].join(" "),
            }),
          )}
        </svg>
      </Box>
    );
  },
);
